import React, {ReactNode} from 'react';
import {formatMessageBundle} from "@unifocus/js-utils";
import messages from "../../../localized-messages";
import {Button} from '../button';
import {Tooltip} from '../../display/tooltip';
import {Cascader} from 'antd';

/**
 * {@docCategory Breadcrumb}
 */
export interface BreadcrumbProps {
   value?: BreadcrumbCascaderValueType;
   onChange?: (value: BreadcrumbCascaderValueType, selectedOptions?: BreadcrumbCascaderOptionType[]) => void;
   displayRender?: (label: string[], selectedOptions?: BreadcrumbCascaderOptionType[]) => React.ReactNode;
   style?: React.CSSProperties;
   popupPlacement?: string;
   placeholder?: string;
   size?: BreadcrumbSizeType;
   name?: string;
   id?: string;
   bordered?: boolean;
   disabled?: boolean;
   allowClear?: boolean;
   autoFocus?: boolean;
   showSearch?: boolean | ShowSearchType;
   notFoundContent?: React.ReactNode;
   loadData?: (selectedOptions?: BreadcrumbCascaderOptionType[]) => void;
   expandTrigger?: CascaderExpandTrigger;
   expandIcon?: React.ReactNode;
   changeOnSelect?: boolean;
   onPopupVisibleChange?: (popupVisible: boolean) => void;
   prefixCls?: string;
   inputPrefixCls?: string;
   getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
   popupVisible?: boolean;
   fieldNames?: FieldNamesType;
   suffixIcon?: React.ReactNode;
   dropdownRender?: (menus: React.ReactNode) => React.ReactNode;
   className?: string;
   popupClassName?: string;
   options: BreadcrumbCascaderOptionType[];
   fieldnames: string[];
   defaultValue?: string[];
   buttonlabel?: string;
   onLoad?: (selectedOptions?: BreadcrumbSelectedOptionsProps) => void;
}

export declare type BreadcrumbCascaderValueType = (string | number)[];

export interface BreadcrumbState {
   breadcrumbChanged: boolean;
}

export interface BreadcrumbCascaderOptionType {
   value?: string | number;
   label?: React.ReactNode;
   disabled?: boolean;
   isLeaf?: boolean;
   loading?: boolean;
   children?: Array<BreadcrumbCascaderOptionType>;
   [key: string]: any;
}

export interface FieldNamesType {
   value?: string | number;
   label?: string;
   children?: string;
}

export declare type BreadcrumbSizeType = 'small' | 'middle' | 'large' | undefined;

export declare type CascaderExpandTrigger = 'click' | 'hover';

export interface ShowSearchType {
   filter?: (inputValue: string, path: BreadcrumbCascaderOptionType[], names: FilledFieldNamesType) => boolean;
   render?: (inputValue: string, path: BreadcrumbCascaderOptionType[], prefixCls: string | undefined, names: FilledFieldNamesType) => React.ReactNode;
   sort?: (a: BreadcrumbCascaderOptionType[], b: BreadcrumbCascaderOptionType[], inputValue: string, names: FilledFieldNamesType) => number;
   matchInputWidth?: boolean;
   limit?: number | false;
}

export interface FilledFieldNamesType {
   value: string | number;
   label: string;
   children: string;
}

export interface BreadcrumbSelectedOptionsProps {
   value: BreadcrumbCascaderValueType;
   selectedOptions?: BreadcrumbCascaderOptionType[];
}

export class Breadcrumb extends React.Component<BreadcrumbProps, BreadcrumbState> {
   state: BreadcrumbState = {
      breadcrumbChanged: false
   };

   private savedBreadcrumbChangeObject: BreadcrumbSelectedOptionsProps | undefined = undefined;

   componentDidMount() {
      this.truncateBreadcrumbPath();
   }

   componentDidUpdate() {
      this.truncateBreadcrumbPath();
   }

   render(): ReactNode {
      const placeholderDefault: string = formatMessageBundle(messages, 'res_clickToSelectOrgLayer');
      const loadButtonTooltipMessage: string = formatMessageBundle(messages, 'res_loadSelectedOrgLayer');
      const defaultButtonLabel: string = formatMessageBundle(messages, 'res_load');
      const className = 'uf-breadcrumb';
      const popupClassName = 'uf-breadcrumb-popup';

      const customFieldNames: FieldNamesType = {
         label: this.props.fieldnames[0],
         value: this.props.fieldnames[1],
         children: this.props.fieldnames[2]
      }

      let sizeObj: unknown = {};
      if (this.props.size) {
         sizeObj = {
            size: this.props.size
         };
      }

      const loadButton: ReactNode = this.renderLoadButton(sizeObj, defaultButtonLabel);

      return (
         <>
            <div
               onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
               const enterKey = 'Enter';

               if (event.key === enterKey) {
                  this.handleLoadRequest(event)
               }
            }
            }
            >
               <Cascader
                  {...this.props}
                  className={`${className} ${this.props.className ? this.props.className : ''}`}
                  popupClassName={`${popupClassName} ${this.props.popupClassName ? this.props.popupClassName : ''}`}
                  style={!this.state.breadcrumbChanged ? {fontWeight: "bold" } : {}}
                  displayRender={label => label.join(' > ')}
                  defaultValue={this.props.defaultValue}
                  placeholder={(this.props.placeholder)?this.props.placeholder: placeholderDefault}
                  changeOnSelect={true}
                  allowClear={false}
                  fieldNames={customFieldNames}
                  onChange={(value: BreadcrumbCascaderValueType, selectedOptions?: BreadcrumbCascaderOptionType[]) => {
                     this.handleOnChange(value, selectedOptions);
                  }}
               />
               {
                  this.state.breadcrumbChanged ?
                  <Tooltip title={loadButtonTooltipMessage}>{loadButton}</Tooltip> : loadButton
               }
            </div>
         </>
      );
   }

   private renderLoadButton = (sizeObj: unknown, defaultButtonLabel: string): ReactNode => {
      return (
         <Button
            className={this.state.breadcrumbChanged ? "load-button-pulse" : ""}
            type='primary'
            disabled={!this.state.breadcrumbChanged}
            onClick={(event: unknown) => this.handleLoadRequest(event)}
            {...sizeObj as any}
         >
            {(this.props.buttonlabel) ? this.props.buttonlabel : defaultButtonLabel}
         </Button>
      );
   }

   private handleOnChange = (value: BreadcrumbCascaderValueType, selectedOptions?: BreadcrumbCascaderOptionType[]): void => {
      this.savedBreadcrumbChangeObject = { value: value, selectedOptions: selectedOptions };

      this.setState({
                       breadcrumbChanged: true
                    });

      const onChange = this.props.onChange;
      if (onChange) {
         onChange(value, selectedOptions);
      }
   }

   private handleLoadRequest = (e: unknown): void => {
      this.setState({
                       breadcrumbChanged: false
                    });

      const onLoad = this.props.onLoad;
      if (onLoad) {
         onLoad(this.savedBreadcrumbChangeObject);
      }
   }

   private calculateTextLengthSubtrahend = (elementWidth: number): number => {
      // breadcrumbPathLabel is set to different widths depending on the screen resolution
      // this method should handle return the correct subtrahend based on element width
      if (elementWidth <= 280 && elementWidth > 245) {
         return 32;
      }
      else if (elementWidth <= 245) {
         return 25;
      }
      else {
         return 36;
      }
   }

   private truncateBreadcrumbPath = () => {
      const breadcrumbPathText: string | null = document.getElementsByClassName("ant-cascader-picker-label")[0].textContent;
      const breadcrumbPathTextLength: number = breadcrumbPathText ? breadcrumbPathText.length : 0;
      const breadcrumbLabelElementWidth: number = document.getElementsByClassName("ant-cascader-picker-label")[0].getBoundingClientRect().width;

      const subtrahendBasedOnElementWidth: number =  this.calculateTextLengthSubtrahend(breadcrumbLabelElementWidth);

      if (breadcrumbPathText) {
         let resultText = '';
         if (breadcrumbPathTextLength <= subtrahendBasedOnElementWidth) {
            resultText = breadcrumbPathText;
         }
         else {
            resultText ='\u2026 ' + breadcrumbPathText.slice(breadcrumbPathTextLength - subtrahendBasedOnElementWidth, breadcrumbPathTextLength);
         }

         document.getElementsByClassName("ant-cascader-picker-label")[0].innerHTML = resultText;
      }
   }
}
